import * as React from 'react';
import { useCallback, useMemo } from 'react';
import Modal from '../../../layout/modal/Modal';
import { DevPlanFieldListItem } from './models';
import ModalHeader from '../../../layout/modal/ModalHeader';
import DevActionListItem from './DevActionListItem';
import { useAppState } from '../../../core/redux/hooks';
import LocalDiv from '../../../localization/LocalDiv';
import LocalBtn from '../../../localization/LocalBtn';
import Spacer from '../../../layout/Spacer';
import { FLEX_ROW, FLEX_COLUMN } from '../../../layout/flexBox';
import { css } from '@emotion/core';
import { btnStyle } from '../../../forms/styles/button';
import { COLORS } from '../../../globalStyle/colors';
import { Str } from '../../../localization/translationKeys';


const styles = {
  root:         [
    FLEX_COLUMN,
  ],
  content:      [
    FLEX_COLUMN,
    css`
      justify-content: space-between;
      flex-grow: 1;
      flex-basis: 100%;
      margin: 30px;
    `,
  ],
  listItems:    [
    FLEX_COLUMN,
  ],
  listItem:     [
    FLEX_ROW,
    css`
      justify-content: space-between;
      padding: 8px;
      border: 1px solid ${COLORS.WHITE};
    `,
  ],
  spacer:       [
    FLEX_COLUMN,
    css`
      flex-grow: 0;
      flex-shrink: 0;
      flex-basis: 100%;
    `,
  ],
  btnRow:       [
    FLEX_ROW,
    css`
      justify-content: flex-end;
    `,
  ],
  secondaryBtn: [
    btnStyle.secondary(COLORS.BLUE),
    css`
      min-width: 135px;
    `,
  ],
  primaryBtn:   [
    btnStyle.primary(COLORS.BLUE),
    css`
      min-width: 135px;
    `,
  ],
};


type Props = {
  devItemCompetencyId?: number;
  dismiss: () => void;
  isSelected: (item: DevPlanFieldListItem) => boolean;
  onClickListItemCheckbox: (item: DevPlanFieldListItem) => void;
  onClickConfirmAdd: () => void;
  className?: string;
}


const renderItems = (
  listItems: DevPlanFieldListItem[],
  isSelected: (item: DevPlanFieldListItem) => boolean,
  toggleSelected: (item: DevPlanFieldListItem) => void,
) => listItems.map(
  item => (
    <DevActionListItem
      key={item.compilationCategoryListItemId}
      listItem={item}
      isSelected={isSelected(item)}
      toggleSelected={() => toggleSelected(item)}
      css={styles.listItem}
    />
  ),
);


/**
 * DevActionListModal: a component that
 */
const DevActionListModal: React.FC<Props> = (
  {
    devItemCompetencyId,
    dismiss,
    isSelected,
    onClickListItemCheckbox,
    onClickConfirmAdd,
    className,
  },
) => {
  const langId    = useAppState(state => state.locale.lang.languageId);
  const listItems = useAppState(state => state.devPlan?.listItems?.[`${langId}`] ?? {});

  // Show only those list items that apply to this dev item's competency.
  const filteredListItems = useMemo(
    () => Object.values(listItems).filter(li => li.compilationCategoryId === devItemCompetencyId),
    [devItemCompetencyId, listItems],
  );

  const handleClickConfirmAdd = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onClickConfirmAdd();
    },
    [onClickConfirmAdd],
  );

  return (
    <Modal dismissable={true} onDismiss={dismiss} css={styles.root}>
      <ModalHeader dismissable={true} onDismiss={dismiss} />
      <LocalDiv css={styles.content}>
        <LocalDiv css={styles.listItems}>
          {renderItems(filteredListItems, isSelected, onClickListItemCheckbox)}
        </LocalDiv>
        <LocalDiv css={styles.btnRow}>
          <LocalBtn
            onClick={dismiss}
            str={Str.Cancel}
            css={styles.secondaryBtn}
          />
          <Spacer sizePx={10} />
          <LocalBtn
            onClick={handleClickConfirmAdd}
            str={Str.Add}
            css={styles.primaryBtn}
          />
        </LocalDiv>
      </LocalDiv>
    </Modal>
  );
};

export default DevActionListModal;
